import _ from "lodash";
import client from "lib/apolloClient/client";
import {
  createPointAssessmentSetMutation,
  deletePointAssessmentSetMutation,
  updatePointAssessmentSetMutation,
  copyPointAssessmentMutation,
  bulkCopyPointAssessmentSetMutation,
} from "./ScoreCategoriesMutation";
import {
  getOrganizationPointSetQuery,
  getSubjectDetailsQuery,
  getAssignmentsWithAutoEvaluateToolQuery,
} from "./ScoreCategoriesQueries";
import { getOrganizationPointSetFromCache } from "ScoreCategories/modules/ScoreCategoriesGraphqlHelpers";
import { colors } from "@toddle-edu/ds-theme";
import { getSettingValue } from "modules/PermissionModule";
import { CURRICULUM_TYPE_DP } from "Constants/stringConstants";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getCategorySets } from "../../../../DPAcademicSetup/routes/AssessmentCategories/modules/AssessmentCategoriesModule";

export const colorPalletOptions = [
  colors.blue400,
  colors.red66,
  colors.violet500,
  colors.teal200,
  colors.pink200,
  colors.neutral400,
  colors.blueTwo,
];

//Helpers

export const scoreSettingTypes = [
  "SCORE_CATEGORY_MYP",
  "SCORE_CATEGORY_PYP",
  "SCORE_CATEGORY_DP",
  "SCORE_CATEGORY_UBD",
];

export const checkCategorySetApplicableTo = ({ organizationId }) => {
  return getSettingValue({
    name: "MapAssessmentSetWith",
    organizationId,
  });
};

export const checkIsScoreEnabledFromAdmin = ({ organizationId }) => {
  return getSettingValue({
    name: "EnableAssessUsingScoreByTeacher",
    organizationId,
  });
};

export const getScoreDetailsMemoize = _.memoize(
  params => getScoreDetails(params),
  params => JSON.stringify(params)
);

const getScoreDetails = ({ assessmentTools }) => {
  const _assessmentTools = !_.isEmpty(assessmentTools?.edges)
    ? assessmentTools?.edges
    : assessmentTools;

  const scoreTool = _.find(_assessmentTools, ({ node } = {}) => {
    return _.get(node, "assessmentToolType") === "SCORE";
  });

  const scoreToolData = _.get(
    scoreTool,
    "node.assessmentToolItem.scoreToolData",
    {}
  );

  const maxScore = _.get(scoreToolData, "maxScore", null);
  const scoreToolId = _.get(scoreToolData, "id");

  return {
    isScoreEnabled: !_.isEmpty(scoreTool),
    maxScore: maxScore ?? "",
    scoreToolId,
  };
};

export const getSelectedGradesMemoize = _.memoize(
  params => getSelectedGrades(params),
  params => JSON.stringify(params)
);

const getSelectedGrades = ({ grades, templateFieldsObj }) => {
  return _.reduce(
    _.get(templateFieldsObj, "courses", []),
    (selectedGrades, gradeId) => {
      const gradeDetails = _.find(grades, ({ id }) => id === gradeId);
      if (gradeDetails) {
        selectedGrades.push({
          label: _.get(gradeDetails, "name", ""),
          value: gradeId,
          key: gradeId,
        });
      }

      return selectedGrades;
    },
    []
  );
};

export const getGradesOptionsMemoize = _.memoize(
  params => getGradesOptions(params),
  params => JSON.stringify(params)
);

const getGradesOptions = ({ grades, usedCourses }) => {
  return _.reduce(
    grades,
    (gradeOptions, grade) => {
      const gradeId = _.get(grade, "id");

      if (!_.includes(usedCourses, gradeId)) {
        gradeOptions.push({
          label: _.get(grade, "name", ""),
          value: gradeId,
          key: gradeId,
        });
      }

      return gradeOptions;
    },
    []
  );
};

const _getUsedCoursesAndCategorySetNames = ({
  organizationPointSet,
  templateFieldsObj = null,
  isCourseApplicableToCategorySet,
}) => {
  const usedCourses = [];
  const usedCategorySetNames = [];

  const entityType = isCourseApplicableToCategorySet ? "Course" : "Grade";

  //Creates array of class ids associated to the category set
  _.forEach(organizationPointSet, ({ node: organizationPoint }) => {
    if (
      templateFieldsObj &&
      templateFieldsObj.label !== organizationPoint.label
    ) {
      usedCategorySetNames.push(organizationPoint.label);
    }
    _.forEach(
      _.get(organizationPoint, "associatedEntities", []),
      associatedEntity => {
        if (_.get(associatedEntity, "__typename", "") === entityType) {
          usedCourses.push(associatedEntity.id);
        }
      }
    );
  });

  return { usedCourses, usedCategorySetNames };
};

export const getUsedCoursesAndCategorySetNames = _.memoize(
  _getUsedCoursesAndCategorySetNames,
  params => JSON.stringify(params)
);

export const areAllCoursesUsed = ({
  organizationPointSet,
  courses,
  courseType,
  idPath,
}) => {
  const usedCourses = [];
  const courseIds = [];
  _.forEach(organizationPointSet, ({ node: organizationPoint }) => {
    _.forEach(
      _.get(organizationPoint, "associatedEntities", []),
      associatedEntity => {
        if (_.get(associatedEntity, "__typename", "") === courseType) {
          usedCourses.push(associatedEntity.id);
        }
      }
    );
  });

  _.forEach(courses, course => {
    const courseId = _.get(course, `${idPath}`, "");
    courseIds.push(courseId);
  });

  return _.isEmpty(_.difference(courseIds, usedCourses));
};

const _getCategorySetsCountBySubjectId = ({ rootNodes, subjectId, termId }) => {
  const subjectNode = _.find(rootNodes, node => node.id == subjectId);
  const pointAssessmentSets = _.get(
    subjectNode,
    "pointAssessmentSets.edges",
    []
  );
  const categorySets = getCategorySets({
    pointAssessmentSets,
    termId,
  });
  return _.size(categorySets);
};

export const getCategorySetsCountBySubjectId = _.memoize(
  _getCategorySetsCountBySubjectId,
  params => JSON.stringify(params)
);

export const getCourseOptionsMemoize = _.memoize(
  params => getCourseOptions(params),
  params => JSON.stringify(params)
);

const getCourseOptions = ({
  subjectBasedCourses,
  organizationPointSet,
  templateFieldsObj,
  t,
}) => {
  let courseOptions = [];

  const templateLabel = templateFieldsObj.label;

  const usedCoursesDetail = [];

  _.forEach(organizationPointSet, ({ node: organizationPoint }) => {
    _.forEach(
      _.get(organizationPoint, "associatedEntities", []),
      associatedEntity => {
        if (_.get(associatedEntity, "__typename", "") === "Course") {
          usedCoursesDetail.push({
            id: associatedEntity.id,
            label: organizationPoint.label,
          });
        }
      }
    );
  });

  _.forEach(_.get(subjectBasedCourses, "courses.edges", []), edge => {
    const node = _.get(edge, "node", {});
    _.forEach(_.get(node, "grades", []), grade => {
      const gradeId = _.get(grade, "id");
      const idx = _.findIndex(
        courseOptions,
        courseOption => _.get(courseOption, "value") === gradeId
      );
      const courseDetail = _.find(
        usedCoursesDetail,
        usedCourse => usedCourse.id === _.get(node, "id")
      );
      const courseTemplateLabel = _.get(courseDetail, "label");

      //Create an object of classes containing class name,id,subText associated with the class
      if (idx !== -1) {
        courseOptions[idx]?.options.push({
          label: _.get(node, "title"),
          value: _.get(node, "id"),
          subText:
            courseTemplateLabel === templateLabel || !courseTemplateLabel
              ? undefined
              : t("academicSetup:score_categories_used_courses_text", {
                  label: courseTemplateLabel,
                }),
        });
      } else {
        courseOptions.push({
          label: _.get(grade, "name"),
          value: _.get(grade, "id"),
          options: [
            {
              label: _.get(node, "title"),
              value: _.get(node, "id"),
              subText:
                courseTemplateLabel === templateLabel || !courseTemplateLabel
                  ? undefined
                  : t("academicSetup:score_categories_used_courses_text", {
                      label: courseTemplateLabel,
                    }),
            },
          ],
        });
      }
    });
  });
  courseOptions = _.sortBy(courseOptions, [
    ({ label }) => _.size(label),
    ({ label }) => label.toUpperCase(),
  ]);
  return courseOptions;
};

export const getUnusedColor = ({ fieldValues }) => {
  let unusedColor = colorPalletOptions[0];
  _.forEach(colorPalletOptions, color => {
    if (
      _.isEmpty(_.find(fieldValues, fieldValue => color === fieldValue.color))
    ) {
      unusedColor = color;
      return;
    }
  });
  return unusedColor ? unusedColor : colorPalletOptions[0];
};

//Function to check if the sum of the category percentage weight is equal to 100% or not
export const checkTotalPercentageWeight = ({
  templateFieldsObj,
  weightKey,
}) => {
  const fieldValues = _.get(templateFieldsObj, "fieldValues", []);
  let totalPercentageWeight = 0;

  _.forEach(fieldValues, fieldValue => {
    totalPercentageWeight =
      totalPercentageWeight + _.toNumber(fieldValue[weightKey]);
  });
  return totalPercentageWeight;
};

export const checkTotalPercentageWeightError = ({
  totalPercentageWeight,
  t,
}) => {
  if (_.isNaN(totalPercentageWeight)) {
    return t("academicSetup:invalid_input");
  }
  if (totalPercentageWeight > 100) {
    const exceededWeight = _.round(totalPercentageWeight - 100, 2);

    return t("academicSetup:exceeded_label", {
      label: `${exceededWeight}%`,
    });
  } else {
    const remainingWeight = _.round(100 - totalPercentageWeight, 2);
    return t("academicSetup:remaining_label", {
      label: `${remainingWeight}%`,
    });
  }
};

//Function to check the invalid input fields error
export const checkTemplateObjError = ({
  templateFieldsObj,
  setAreNotValid,
  curriculumType,
  subjectLevelGenericTags,
  excludeWeightCheck = false,
}) => {
  let errorCount = 0;
  let errorObj = {};
  if (_.size(templateFieldsObj.label) === 0) {
    errorCount = errorCount + 1;
    errorObj = { ...errorObj, setLabel: true };
  }
  if (_.size(templateFieldsObj.courses) === 0) {
    errorCount = errorCount + 1;
    errorObj = { ...errorObj, courses: true };
  }
  _.forEach(templateFieldsObj.fieldValues, (fieldValue, index) => {
    if (_.size(fieldValue.label) === 0) {
      errorCount = errorCount + 1;
      errorObj = { ...errorObj, [`label_${index}`]: true };
    }
    if (
      !excludeWeightCheck &&
      curriculumType !== "IB_DP" &&
      fieldValue.percentage_weight === ""
    ) {
      errorCount = errorCount + 1;
      errorObj = { ...errorObj, [`percentage_weight_${index}`]: true };
    }
    if (!excludeWeightCheck && curriculumType === "IB_DP") {
      _.forEach(subjectLevelGenericTags, tag => {
        const tagLabel = _.get(tag, "label", "");
        const weight = _.isEqual(tagLabel, "SL", "")
          ? fieldValue.slWeight
          : fieldValue.hlWeight;

        if (weight === "") {
          errorCount = errorCount + 1;
          errorObj = {
            ...errorObj,
            [`${_.toLower(tagLabel)}_weight_${index}`]: true,
          };
        }
      });
    }
  });

  setAreNotValid(errorObj);

  return errorCount;
};

export const getCurriculumBasedAdminChildId = ({ curriculumType }) => {
  switch (curriculumType) {
    case "IB_MYP":
      return "MYP_SCORE_CATEGORIES";
    case "IB_PYP":
      return "PYP_SCORE_CATEGORIES";
    case "UBD":
      return "UBD_SCORE_CATEGORIES";
    case "IB_DP":
      return "DP_SCORE_CATEGORIES";
  }
};

export const getHierarchy = ({
  slice,
  subjectId,
  subjectName,
  t,
  curriculumProgramTitle,
}) => {
  const hierarchy = [
    {
      id: "HOME",
      label: curriculumProgramTitle,
    },
    {
      id: "ACADEMIC_CRITERIA",
      label: t("teacherHomePage:academic_setup"),
    },
    {
      id: "SCORE_CATEGORIES",
      label: t("common:point_based_assessment"),
    },
    {
      id: `${subjectId}`,
      label: subjectName,
    },
  ];
  return _.slice(hierarchy, 0, slice);
};

export const getCurriculumBasedRoute = ({ curriculumType }) => {
  switch (curriculumType) {
    case "IB_MYP":
      return "mypAcademicSetup";
    case "IB_PYP":
      return "academicSetup";
    case "UBD":
      return "ubdAcademicSetup";
    case "IB_DP":
      return "dpAcademicSetup";
  }
};

export const getHierarchyRoute = ({
  id,
  curriculumType,
  curriculumProgramId,
}) => {
  switch (id) {
    case "HOME":
    case "ACADEMIC_CRITERIA":
      return `administrator`;
    case "SCORE_CATEGORIES":
      return `administrator/${curriculumProgramId}/${getCurriculumBasedRoute({
        curriculumType,
      })}/score-categories`;
    default:
      return `administrator/${curriculumProgramId}/${getCurriculumBasedRoute({
        curriculumType,
      })}/score-categories/${id}`;
  }
};

const getCategoryValueInputList = ({
  hlWeight,
  slWeight,
  percentage_weight,
  curriculumType,
  subjectLevelGenericTags,
}) => {
  const isCurriculumDP = _.isEqual(curriculumType, CURRICULUM_TYPE_DP);
  const categoryValueInputList = [];
  if (
    !isCurriculumDP ||
    (isCurriculumDP && _.isEmpty(subjectLevelGenericTags))
  ) {
    categoryValueInputList.push({
      value: parseFloat(percentage_weight),
      valueType: "FIXED",
      weightageType: "PERCENTAGE",
    });
  } else {
    _.forEach(subjectLevelGenericTags, tag => {
      const tagLabel = _.get(tag, "label", "");
      const weight = _.isEqual(tagLabel, "SL") ? slWeight : hlWeight;

      categoryValueInputList.push({
        value: parseFloat(weight),
        genericTagId: tag?.id,
        valueType: "FIXED",
        weightageType: "PERCENTAGE",
      });
    });
  }
  return categoryValueInputList;
};

const getUpdatedCategoryValuesList = ({
  hlWeight,
  slWeight,
  categoryId,
  percentage_weight,
  curriculumType,
  subjectLevelGenericTags,
  scoreCategorySet,
}) => {
  const isCurriculumDP = _.isEqual(curriculumType, CURRICULUM_TYPE_DP);
  const addedCategorieValues = [];
  const updatedCategorieValues = [];

  const categories = _.get(scoreCategorySet, "categories", []);

  const currentCategoryById = _.find(
    categories,
    category => category?.id === categoryId
  );

  const values = _.get(currentCategoryById, "values", []);

  if (
    !isCurriculumDP ||
    (isCurriculumDP && _.isEmpty(subjectLevelGenericTags))
  ) {
    _.forEach(values, value => {
      const categoryValueId = _.get(value, "id");
      if (categoryValueId) {
        updatedCategorieValues.push({
          id: categoryValueId,
          value: parseFloat(percentage_weight),
          valueType: value?.valueType,
          weightageType: value?.weightageType,
        });
      } else {
        addedCategorieValues.push({
          value: parseFloat(percentage_weight),
          valueType: value?.valueType,
          weightageType: value?.weightageType,
        });
      }
    });
  } else {
    _.forEach(subjectLevelGenericTags, tag => {
      const tagLabel = _.get(tag, "label", "");
      const weight = _.isEqual(tagLabel, "SL") ? slWeight : hlWeight;
      const categoryValue = _.find(
        values,
        value => value?.genericTag?.id === tag?.id
      );

      if (categoryValue) {
        const categoryValueId = _.get(categoryValue, "id");
        updatedCategorieValues.push({
          id: categoryValueId,
          value: parseFloat(weight),
          genericTagId: tag?.id,
          valueType: "FIXED",
          weightageType: "PERCENTAGE",
        });
      } else {
        addedCategorieValues.push({
          value: parseFloat(weight),
          genericTagId: tag?.id,
          valueType: "FIXED",
          weightageType: "PERCENTAGE",
        });
      }
    });
  }

  return {
    addedCategorieValues,
    updatedCategorieValues,
  };
};

//Mutations
export const addScoreCategoryTemplateNode = ({
  variables,
  termId = null,
  subjectId,
  templateFieldsObj,
  curriculumProgramId,
  curriculumType,
  subjectLevelGenericTags,
  isCourseApplicableToCategorySet,
  scoreBasedAssessmentVariables,
  isScoreV2,
  portalType,
}) => {
  return async (dispatch, getState) => {
    const entityType = isCourseApplicableToCategorySet ? "COURSE" : "GRADE";

    const courses = _.map(_.get(templateFieldsObj, "courses", []), course => ({
      type: entityType,
      id: course,
    }));
    const associatedEntites = [{ type: "SUBJECT", id: subjectId }, ...courses];
    if (termId) {
      associatedEntites.push({ type: "ACADEMIC_TERM", id: termId });
    }

    const categories = _.map(
      _.get(templateFieldsObj, "fieldValues", []),
      fieldValue => {
        const {
          label,
          color,
          percentage_weight,
          hlWeight,
          slWeight,
          gradeBoundaryId = null,
          gradeScaleType = null,
          displaySequence,
        } = fieldValue;

        const categoryValueInputList = getCategoryValueInputList({
          slWeight,
          hlWeight,
          percentage_weight,
          curriculumType,
          subjectLevelGenericTags,
        });

        return {
          label,
          colorCode: color,
          gradeBoundaryId,
          gradeScaleType,
          displaySequence,
          values: categoryValueInputList,
        };
      }
    );

    const scoreCategoryTemplateVariables = {
      curriculumProgramId,
      label: _.get(templateFieldsObj, "label", ""),
      isShowGradeBoundaries: _.get(
        templateFieldsObj,
        "isShowGradeBoundaries",
        false
      ),
      type: "SCORE",
      associatedEntites,
      categories,
    };

    const refetchQueries = [
      {
        query: getOrganizationPointSetQuery,
        variables,
      },
    ];

    if (isScoreV2) {
      refetchQueries.push({
        query: getSubjectDetailsQuery,
        variables: scoreBasedAssessmentVariables,
      });
    }

    return await client.mutate({
      mutation: createPointAssessmentSetMutation,
      variables: { input: [scoreCategoryTemplateVariables], portalType },
      awaitRefetchQueries: true,
      refetchQueries,
    });
  };
};

export const deleteScoreCategoryTemplateNode = ({
  variables,
  templateId,
  scoreBasedAssessmentVariables,
  isScoreV2,
  portalType,
}) => {
  const refetchQueries = [];

  if (!_.isEmpty(variables)) {
    refetchQueries.push({
      query: getOrganizationPointSetQuery,
      variables,
    });
  }

  if (isScoreV2) {
    refetchQueries.push({
      query: getSubjectDetailsQuery,
      variables: scoreBasedAssessmentVariables,
    });
  }

  return async (dispatch, getState) => {
    return await client.mutate({
      mutation: deletePointAssessmentSetMutation,
      variables: {
        input: {
          ids: [templateId],
        },
        portalType,
      },
      awaitRefetchQueries: true,
      refetchQueries,
    });
  };
};

export const updateScoreCategoryTemplateNode = ({
  variables,
  templateFieldsObj,
  scoreCategorySet,
  subjectId,
  isCourseApplicableToCategorySet,
  curriculumType,
  subjectLevelGenericTags,
  portalType,
  overriddenCategories,
}) => {
  return async (dispatch, getState) => {
    const entityType = isCourseApplicableToCategorySet ? "COURSE" : "GRADE";

    const scoreSet = _.filter(
      _.get(scoreCategorySet, "associatedEntities", []),
      associatedEntity =>
        !_.isEqual(_.get(associatedEntity, "__typename"), "AcademicTerm") &&
        associatedEntity.id !== subjectId
    );

    const removedCourses = [];
    const addedCourses = [];
    _.forEach(scoreSet, associatedParent => {
      if (
        _.isEmpty(
          _.find(
            _.get(templateFieldsObj, "courses", []),
            course => course === associatedParent.id
          )
        )
      ) {
        removedCourses.push({ type: entityType, id: associatedParent.id });
      }
    });
    _.forEach(_.get(templateFieldsObj, "courses", []), course => {
      if (
        _.isEmpty(
          _.find(scoreSet, associatedParent => associatedParent.id === course)
        )
      ) {
        addedCourses.push({ type: entityType, id: course });
      }
    });

    const addedCategories = [];
    const removedCategories = [];
    const updatedCategories = [];

    _.forEach(_.get(templateFieldsObj, "fieldValues", []), fieldValue => {
      if (_.isNull(_.get(fieldValue, "id", null))) {
        const categoryValueInputList = getCategoryValueInputList({
          slWeight: _.get(fieldValue, "slWeight", ""),
          hlWeight: _.get(fieldValue, "hlWeight", ""),
          percentage_weight: _.get(fieldValue, "percentage_weight"),
          curriculumType,
          subjectLevelGenericTags,
        });

        addedCategories.push({
          label: _.get(fieldValue, "label"),
          colorCode: _.get(fieldValue, "color"),
          gradeBoundaryId: _.get(fieldValue, "gradeBoundaryId"),
          gradeScaleType: _.get(fieldValue, "gradeScaleType"),
          displaySequence: _.get(fieldValue, "displaySequence"),
          values: categoryValueInputList,
        });
      } else if (
        !_.isEmpty(
          _.find(
            _.get(scoreCategorySet, "categories", []),
            scoreCategory => scoreCategory.id == fieldValue.id
          )
        )
      ) {
        const updatedCategorieValuesList = getUpdatedCategoryValuesList({
          slWeight: _.get(fieldValue, "slWeight", ""),
          hlWeight: _.get(fieldValue, "hlWeight", ""),
          categoryId: _.get(fieldValue, "id", ""),
          percentage_weight: _.get(fieldValue, "percentage_weight"),
          curriculumType,
          subjectLevelGenericTags,
          scoreCategorySet,
        });

        updatedCategories.push({
          id: fieldValue.id,
          label: _.get(fieldValue, "label"),
          colorCode: _.get(fieldValue, "color"),
          gradeBoundaryId: _.get(fieldValue, "gradeBoundaryId"),
          gradeScaleType: _.get(fieldValue, "gradeScaleType"),
          displaySequence: _.get(fieldValue, "displaySequence"),
          values: updatedCategorieValuesList,
          removeOverridenCategoryFinalScores: _.some(
            overriddenCategories,
            overriddenCategory =>
              _.get(overriddenCategory, "id") === _.get(fieldValue, "id")
          ),
        });
      }
    });

    _.forEach(_.get(scoreCategorySet, "categories", []), scoreCategory => {
      if (
        _.isEmpty(
          _.find(
            _.get(templateFieldsObj, "fieldValues", []),
            fieldValue => fieldValue.id === scoreCategory.id
          )
        )
      ) {
        removedCategories.push(scoreCategory.id);
      }
    });

    const scoreCategoryTemplateVariables = {
      id: templateFieldsObj.id,
      label: _.get(templateFieldsObj, "label", ""),
      isShowGradeBoundaries: _.get(
        templateFieldsObj,
        "isShowGradeBoundaries",
        false
      ),
      associatedEntities: {
        addedAssociatedEntities: _.isEmpty(addedCourses)
          ? undefined
          : addedCourses,
        removedAssociatedEntities: _.isEmpty(removedCourses)
          ? undefined
          : removedCourses,
      },
      categories: {
        addedCategories: _.isEmpty(addedCategories)
          ? undefined
          : addedCategories,
        removedCategories: _.isEmpty(removedCategories)
          ? undefined
          : removedCategories,
        updatedCategories: _.isEmpty(updatedCategories)
          ? undefined
          : updatedCategories,
      },
    };

    return await client.mutate({
      mutation: updatePointAssessmentSetMutation,
      variables: { input: [scoreCategoryTemplateVariables], portalType },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: getOrganizationPointSetQuery,
          variables,
        },
      ],
    });
  };
};

export const addCategoryGradeBoundary = ({
  categoryGradeBoundaryValues,
  subjectLevelGenericTags,
  curriculumProgramId,
  portalType,
}) => {
  return async (dispatch, getState) => {
    const categories = _.map(categoryGradeBoundaryValues, item => {
      const grade = _.get(item, "grade", "");

      return {
        label: _.toString(grade),
        values: _.map(
          !_.isEmpty(subjectLevelGenericTags)
            ? subjectLevelGenericTags
            : [{ label: "SL" }],
          tag => {
            const tagLabel = _.get(tag, "label", "");
            const scoreRange = _.get(item, tagLabel, {});

            return {
              minValue: _.toNumber(_.get(scoreRange, "minValue", "")),
              maxValue: _.toNumber(_.get(scoreRange, "maxValue", "")),
              genericTagId: _.get(tag, "id", null),
              valueType: "RANGE",
              weightageType: "NUMBER",
            };
          }
        ),
      };
    });

    const categoryGradeBoundaryInput = {
      curriculumProgramId,
      type: "GRADE_BOUNDARY",
      categories,
    };

    let response;
    try {
      const res = await client.mutate({
        mutation: createPointAssessmentSetMutation,
        variables: { input: [categoryGradeBoundaryInput], portalType },
      });

      response = _.first(
        _.get(res, "data.planner.createPointAssessmentSet.response")
      );
    } catch {
      response = null;
    }
    return response;
  };
};

export const updateCategoryGradeBoundary = ({
  categoryGradeBoundaryValues,
  subjectLevelGenericTags,
  gradeBoundaryId,
  extraParams = {},
  portalType,
}) => {
  return async (dispatch, getState) => {
    const updatedCategories = _.map(categoryGradeBoundaryValues, item => {
      const grade = _.get(item, "grade", "");

      const addedCategorieValues = [];
      const updatedCategorieValues = [];

      _.forEach(
        !_.isEmpty(subjectLevelGenericTags)
          ? subjectLevelGenericTags
          : [{ label: "SL" }],
        tag => {
          const tagLabel = _.get(tag, "label", "");
          const scoreRange = _.get(item, tagLabel, {});

          const valueId = _.get(scoreRange, "id");

          const valueObj = {
            minValue: _.toNumber(_.get(scoreRange, "minValue", "")),
            maxValue: _.toNumber(_.get(scoreRange, "maxValue", "")),
            genericTagId: _.get(tag, "id", null),
            valueType: "RANGE",
            weightageType: "NUMBER",
          };

          if (!_.isEmpty(valueId)) {
            updatedCategorieValues.push({ id: valueId, ...valueObj });
          } else {
            addedCategorieValues.push(valueObj);
          }
        }
      );

      return {
        id: _.get(item, "id"),
        label: _.toString(grade),
        values: {
          addedCategorieValues,
          updatedCategorieValues,
        },
      };
    });

    const categoryGradeBoundaryInput = {
      id: gradeBoundaryId,
      categories: { updatedCategories },
      ...extraParams,
    };

    return await client.mutate({
      mutation: updatePointAssessmentSetMutation,
      variables: { input: [categoryGradeBoundaryInput], portalType },
    });
  };
};

export const copyPointAssessment = ({
  id,
  label,
  entitesForCopySet,
  curriculumProgramId,
  pointSetVariables,
  type = "ASSESSMENT_CATEGORY",
  scoreBasedAssessmentVariables,
  subType,
}) => {
  return async (dispatch, getState) => {
    const copyPointAssessmentInput = {
      id,
      label,
      curriculumProgramId,
      entitesForCopySet,
      type,
      subType,
    };

    return await client.mutate({
      mutation: copyPointAssessmentMutation,
      variables: { input: copyPointAssessmentInput },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: getOrganizationPointSetQuery,
          variables: pointSetVariables,
        },
        {
          query: getSubjectDetailsQuery,
          variables: scoreBasedAssessmentVariables,
        },
      ],
    });
  };
};

export const bulkCopyPointAssessmentSet = ({
  sourceTermId,
  targetTermId,
  criteriaType,
  associatedEntities,
  dataSource,
  isOverride,
  isCopyFromSheet,
  curriculumProgramId,
  scoreBasedAssessmentVariables,
  pointSetVariables,
  toastMessage,
  localeParams = [],
}) => {
  return async (dispatch, getState) => {
    const organizationId = _.get(getState(), "login.userInfo.org_id");

    const refetchQueries = [
      {
        query: getSubjectDetailsQuery,
        variables: scoreBasedAssessmentVariables,
      },
    ];

    if (!_.isEmpty(pointSetVariables)) {
      refetchQueries.push({
        query: getOrganizationPointSetQuery,
        variables: pointSetVariables,
      });
    }

    try {
      const response = await client.mutate({
        mutation: bulkCopyPointAssessmentSetMutation,
        variables: {
          input: {
            sourceTermId,
            targetTermIds: _.castArray(targetTermId),
            criteriaType,
            organizationId,
            associatedEntities,
            dataSource,
            isOverride,
            isCopyFromSheet,
            curriculumProgramId,
          },
        },
        awaitRefetchQueries: true,
        refetchQueries,
      });

      const isSuccess = _.get(
        response,
        "data.planner.bulkCopyPointAssessmentSet.isSuccess",
        false
      );

      if (isSuccess && !_.isEmpty(toastMessage)) {
        dispatch(
          setToastMsg({
            msg: toastMessage,
            locale_params: localeParams,
            type: "success",
          })
        );
      }
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const getAssignmentsWithAutoEvaluateTool = () => {
  return async (dispatch, getState) => {
    const orgId = _.get(getState(), "login.userInfo.org_id");

    try {
      const response = await client.query({
        query: getAssignmentsWithAutoEvaluateToolQuery,
        variables: { id: orgId },
        fetchPolicy: "network-only",
      });

      return _.get(response, "data.node", []);
    } catch (e) {
      return {};
    }
  };
};

const _getAssessmentCategorySetStructure = ({
  scoreCategorySet,
  isCourseApplicableToCategorySet,
}) => {
  //Update templateFieldsObj values in the redux state
  const set = {};
  const categories = _.orderBy(
    _.get(scoreCategorySet, "categories", []),
    "displaySequence",
    "asc"
  );
  set["fieldValues"] = _.map(categories, category => {
    return {
      id: _.get(category, "id", ""),
      label: _.get(category, "label", ""),
      color: _.get(category, "colorCode", ""),
      percentage_weight: _.get(category, "percentageWeight", ""),
      hlWeight: _.get(category, "hlWeight", ""),
      slWeight: _.get(category, "slWeight", ""),
      gradeBoundaryId: _.get(category, "gradeBoundary.id", null),
      gradeScaleType: _.get(category, "gradeScaleType", null),
      displaySequence: _.get(category, "displaySequence", 1),
    };
  });

  //Update courses associated to the category set in the redux state(templateFieldsObj)
  const entityType = isCourseApplicableToCategorySet ? "Course" : "Grade";
  const courses = [];
  _.forEach(
    _.get(scoreCategorySet, "associatedEntities", []),
    associatedEntitiy => {
      if (_.get(associatedEntitiy, "__typename", "") === entityType) {
        courses.push(associatedEntitiy.id);
      }
    }
  );
  set["id"] = _.get(scoreCategorySet, "id", "");
  set["label"] = _.get(scoreCategorySet, "label", "");
  set["isShowGradeBoundaries"] = _.get(
    scoreCategorySet,
    "isShowGradeBoundaries",
    false
  );
  set["courses"] = courses;
  return set;
};

export const getAssessmentCategorySetStructure = _.memoize(
  _getAssessmentCategorySetStructure,
  params => JSON.stringify(params)
);

export const getIsCategorySetUpdated = ({
  pointSetVariables,
  templateFieldsObj,
  isCourseApplicableToCategorySet,
}) => {
  const organizationPointSetData = getOrganizationPointSetFromCache({
    variables: pointSetVariables,
  });
  const organizationPointSet = _.get(
    organizationPointSetData,
    "pointAssessmentSets.edges",
    []
  );
  const scoreCategorySet = _.get(
    _.find(
      organizationPointSet,
      ({ node }) => node.id === _.get(templateFieldsObj, "id")
    ),
    "node",
    {}
  );
  const set = getAssessmentCategorySetStructure({
    scoreCategorySet,
    isCourseApplicableToCategorySet,
  });
  return !_.isEqual(set, templateFieldsObj);
};

const _getIsCategorySetNotEmpty = ({ templateFieldsObj }) => {
  if (
    !_.isEmpty(templateFieldsObj?.label) &&
    !_.isEmpty(templateFieldsObj?.courses)
  ) {
    return true;
  }
  const fieldValues = _.get(templateFieldsObj, "fieldValues", []);
  let areFieldsNotEmpty = false;
  _.forEach(fieldValues, fieldValue => {
    areFieldsNotEmpty =
      !_.isEmpty(fieldValue?.label) ||
      !_.isEmpty(fieldValue?.percentage_weight) ||
      !_.isEmpty(fieldValue?.hlWeight) ||
      !_.isEmpty(fieldValue?.slWeight);
    if (areFieldsNotEmpty) {
      return false;
    }
  });
  return areFieldsNotEmpty;
};

export const getIsCategorySetNotEmpty = _.memoize(
  _getIsCategorySetNotEmpty,
  params => JSON.stringify(params)
);

const IB_GRADE_SCALE = [1, 2, 3, 4, 5, 6, 7];
const IB_TOK_GRADE_SCALE = ["E", "D", "C", "B", "A"];

export const getCategoryGradeBoundaryInitialValues = ({ subjectType }) => {
  const isTOKSubject = _.isEqual(subjectType, "TOK");
  const gradeScale = isTOKSubject ? IB_TOK_GRADE_SCALE : IB_GRADE_SCALE;
  return gradeScale.map(grade => ({
    grade,
    SL: {
      minValue: _.isEqual(grade, isTOKSubject ? "E" : 1) ? "0" : "",
      maxValue: "",
    },
    HL: {
      minValue: _.isEqual(grade, isTOKSubject ? "E" : 1) ? "0" : "",
      maxValue: "",
    },
  }));
};

export const showToastAfterDuplicatingBoundariesToGrade = ({
  gradeLabel,
  t,
}) => {
  return (dispatch, getState) => {
    dispatch(
      setToastMsg({
        msg: "toastMsgs:successfully_duplicated_template_item_to_label",
        type: "success",
        locale_params: [
          {
            key: "templateItem",
            value: t("academicSetup:grade_boundaries"),
            isPlainText: true,
          },
          {
            key: "label",
            value: gradeLabel,
            isPlainText: true,
          },
        ],
      })
    );
  };
};

export const showToastAfterImportingTemplates = ({
  isImportFromAnotherTerm,
  parentType,
  t,
  targetTermLabel,
  selectedSubjectsCount,
  templatesCount,
}) => {
  return (dispatch, getState) => {
    let toastMsg = "";
    let localeParamsTemplateItemValue = "";
    let localeParams = [];

    if (parentType === "GRADE_SCALES") {
      if (isImportFromAnotherTerm) {
        toastMsg =
          "academicSetup:grade_boundaries_successfully_imported_to_label_with_count";
      } else {
        toastMsg =
          "academicSetup:grade_boundary_successfully_duplicated_to_subjects_and_grade_levels_with_count";
      }

      localeParams = [
        {
          key: "count",
          value: templatesCount,
          isPlainText: true,
        },
        {
          key: "label",
          value: targetTermLabel,
          isPlainText: true,
        },
      ];
    } else {
      if (isImportFromAnotherTerm) {
        toastMsg = "toastMsgs:successfully_imported_template_item_for_label";
        localeParamsTemplateItemValue =
          parentType === "ASSESSMENT_CATEGORIES"
            ? t("academicSetup:category_sets")
            : t("academicSetup:grade_boundaries");
      } else {
        toastMsg = "toastMsgs:successfully_duplicated_template_item_to_label";
        localeParamsTemplateItemValue =
          parentType === "ASSESSMENT_CATEGORIES"
            ? t("academicSetup:category_set")
            : t("academicSetup:grade_boundaries");
      }
      const localeParamsLabelValueLocaleKey =
        selectedSubjectsCount > 1
          ? "academicSetup:subject_count_plural"
          : "academicSetup:subject_count";
      localeParams = [
        {
          key: "templateItem",
          value: localeParamsTemplateItemValue,
          isPlainText: true,
        },
        {
          key: "label",
          value: t(localeParamsLabelValueLocaleKey, {
            count: selectedSubjectsCount,
          }),
          isPlainText: true,
        },
      ];
    }
    dispatch(
      setToastMsg({
        type: "success",
        msg: toastMsg,
        locale_params: localeParams,
      })
    );
  };
};

export const showToastAfterDuplicatingCategorySetsToGrade = ({
  selectedGrades,
  t,
  grades,
  modalState,
  selectedTerms = [],
}) => {
  return (dispatch, getState) => {
    let localeParamsLabelValue = "";
    if (modalState === "DUPLICATE_TO_OTHER_GRADES") {
      const selectedGradesCount = _.size(selectedGrades);
      if (selectedGradesCount === 1) {
        localeParamsLabelValue = _.get(
          _.find(grades, grade => grade?.id === _.first(selectedGrades)),
          "name"
        );
      } else if (selectedGradesCount > 1) {
        localeParamsLabelValue = `${selectedGradesCount} ${t(
          "common:grades_lowercase"
        )}`;
      }
    } else {
      const selectedTermsCount = _.size(selectedTerms);
      localeParamsLabelValue = `${selectedTermsCount} ${t("common:lowercase", {
        text: t("common:term_plural"),
      })}`;
    }
    dispatch(
      setToastMsg({
        type: "success",
        msg: "toastMsgs:successfully_duplicated_template_item_to_label",
        locale_params: [
          {
            key: "label",
            value: localeParamsLabelValue,
            isPlainText: true,
          },
          {
            key: "templateItem",
            value: t("academicSetup:category_sets"),
            isPlainText: true,
          },
        ],
      })
    );
  };
};

export const updateScoreCategoryGradeScaleType = ({
  categorySetId,
  isShowGradeBoundaries,
  categoryId,
  gradeScaleType,
}) => {
  return async () => {
    const variables = {
      input: [
        {
          id: categorySetId,
          isShowGradeBoundaries,
          categories: {
            updatedCategories: [
              {
                id: categoryId,
                gradeScaleType: gradeScaleType,
                removeOverridenCategoryFinalScores: false,
              },
            ],
          },
        },
      ],
    };

    return await client.mutate({
      mutation: updatePointAssessmentSetMutation,
      variables,
    });
  };
};
